import React from "react";

export default function LightingPage() {
    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <header className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-[#063970] mb-4">Industrial and Construction Lighting Solutions</h1>
                        <p className="text-lg text-[#000] max-w-3xl mx-auto">
                            Comprehensive lighting solutions for industrial facilities and construction sites, featuring energy-efficient LED technology.
                        </p>
                    </header>

                    {/* Introduction */}
                    <section className="bg-white rounded-xl shadow-md p-6 mb-8">
                        <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
                                <h2 className="text-2xl font-semibold text-[#063970] mb-4">Modern Lighting Technology</h2>
                                <p className="text-gray-600">
                                    Industrial and construction lighting can be categorized by application, fixture type, and light source.
                                    Light Emitting Diode (LED) technology is prevalent in modern industrial and construction lighting due to
                                    its energy efficiency and durability.
                                </p>
                            </div>
                            <div className="md:w-1/3 flex justify-center">
                                <div className="w-48 h-48 bg-blue-100 rounded-full flex items-center justify-center">
                                    <svg className="w-24 h-24 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Industrial Lighting Section */}
                        <section className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center mb-6">
                                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">Industrial Lighting</h2>
                            </div>

                            <p className="text-gray-600 mb-6">
                                Industrial lighting illuminates factories, warehouses, and other large commercial spaces.
                                The primary focus is on visibility, efficiency, and safety.
                            </p>

                            <h3 className="text-xl font-semibold text-gray-700 mb-4">Sub Categories</h3>

                            <div className="space-y-6">
                                <div className="border-l-4 border-blue-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">High Bay Lighting</h4>
                                    <p className="text-gray-600 text-sm">Used for ceilings 20 feet or higher. It is a powerful, intense light designed for large, open areas like warehouses, manufacturing plants, and gyms.</p>
                                    <ul className="mt-2 space-y-1">
                                        <li className="flex items-start">
                                            <span className="text-blue-500 mr-2">•</span>
                                            <span className="text-gray-600"><span className="font-medium">Linear High Bay:</span> Long, strip-like fixtures for aisles and assembly lines.</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-blue-500 mr-2">•</span>
                                            <span className="text-gray-600"><span className="font-medium">UFO High Bay:</span> Circular fixtures that provide concentrated light.</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="border-l-4 border-green-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Low Bay Lighting</h4>
                                    <p className="text-gray-600 text-sm">Used for ceilings under 20 feet. It concentrates light in specific areas, making it ideal for precision work in electronics or medical facilities.</p>
                                </div>

                                <div className="border-l-4 border-purple-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Vapor Tight Lights</h4>
                                    <p className="text-gray-600 text-sm">Sealed to protect against moisture, dust, and corrosive fumes. These are used in harsh environments such as refrigerated warehouses, food processing plants, and car washes.</p>
                                </div>

                                <div className="border-l-4 border-red-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Explosion-Proof Lighting</h4>
                                    <p className="text-gray-600 text-sm">Designed to withstand the intense heat and pressure of an explosion. They are used in hazardous locations like mining fields, oil rigs, and chemical plants.</p>
                                </div>

                                <div className="border-l-4 border-yellow-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Industrial Strip Lighting</h4>
                                    <p className="text-gray-600 text-sm">Long, narrow fixtures that provide uniform illumination for production lines, workstations, and warehouse shelving.</p>
                                </div>

                                <div className="border-l-4 border-indigo-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Streetlights</h4>
                                    <p className="text-gray-600 text-sm">Provide outdoor lighting for industrial roads, parking lots, and building perimeters.</p>
                                </div>

                                <div className="border-l-4 border-teal-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Floodlights</h4>
                                    <p className="text-gray-600 text-sm">Illuminate wide areas, both indoors and outdoors. They can be found in parking lots, loading docks, and other large industrial yards.</p>
                                </div>
                            </div>
                        </section>

                        {/* Construction Lighting Section */}
                        <section className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center mb-6">
                                <div className="bg-orange-100 p-3 rounded-lg mr-4">
                                    <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">Construction Lighting</h2>
                            </div>

                            <p className="text-gray-600 mb-6">
                                Construction lighting is often temporary and portable, designed for durability and safety in harsh, changing environments.
                            </p>

                            <h3 className="text-xl font-semibold text-gray-700 mb-4">Sub Categories</h3>

                            <div className="space-y-6">
                                <div className="border-l-4 border-orange-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Portable Lighting Towers</h4>
                                    <p className="text-gray-600 text-sm">Large, wheeled units that use high-intensity lights on a mast to illuminate vast outdoor areas. Many modern towers use energy-efficient LED bulbs.</p>
                                </div>

                                <div className="border-l-4 border-blue-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">LED Work Lights</h4>
                                    <p className="text-gray-600 text-sm">Portable, durable, and energy-efficient lights for specific work areas. They are available in many forms, including tripods and magnetic mounts.</p>
                                </div>

                                <div className="border-l-4 border-green-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">String Lights (Festoon)</h4>
                                    <p className="text-gray-600 text-sm">Use multiple lamps on a single cable to provide illumination for walkways, temporary shelters, and other general areas on a job site.</p>
                                </div>

                                <div className="border-l-4 border-purple-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Balloon Lights</h4>
                                    <p className="text-gray-600 text-sm">Inflatable, diffused lights that provide 360-degree, glare-free illumination over a wide area. They are often used for general ambient light on construction sites.</p>
                                </div>

                                <div className="border-l-4 border-red-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Auxiliary Lighting</h4>
                                    <p className="text-gray-600 text-sm">High-visibility lighting used to highlight hazards or illuminate interior areas.</p>
                                </div>

                                <div className="border-l-4 border-yellow-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Gantry Lighting</h4>
                                    <p className="text-gray-600 text-sm">Illuminates large sections of a site, often from a higher vantage point.</p>
                                </div>

                                <div className="border-l-4 border-indigo-500 pl-4 py-2">
                                    <h4 className="font-medium text-gray-800">Hand-Held Lights</h4>
                                    <p className="text-gray-600 text-sm">Durable flashlights and inspection spotlights for detailed work and checking hard-to-reach areas.</p>
                                </div>
                            </div>

                            {/* Flashlights and Torches Section */}
                            <div className="mt-8">
                                <h3 className="text-xl font-semibold text-gray-700 mb-4">Flashlights and Torches</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Headlamps</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Everyday Carry Lights</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Keychain Flashlights</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Tactical Flashlights</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Searchlights</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <span className="text-gray-700">Camping Flashlights</span>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center col-span-2">
                                        <span className="text-gray-700">Charger and Batteries</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Footer */}
                    {/* <footer className="mt-12 text-center text-gray-500 text-sm">
                        <p>© {new Date().getFullYear()} Industrial & Construction Lighting Solutions. All rights reserved.</p>
                    </footer> */}
                </div>
            </div>
       

        </>
    );
}
